<template>
  <div class="general-script-main">
    <h3>
      <span>{{$t('message.script_config_network') | title}}</span>
      <span class="wiki-link">
        <a :href="$t('message.script_config_network_doc_link')" target="_blank">
          WIKI
        </a>
      </span>
    </h3>

    <div v-if="initConfig && initConfig.mode == 'cluster'" class="cluster-info">
      {{ $t('message.will_run_on_this_cluster_node_only') | title }}
    </div>

    <div v-if="description" class="description">{{description}}</div>

    <b-form v-if="initConfig && networkConfig">
      <b-form-row>
        <div class="col-5">
          <span class="label label-default">
            {{$t('message.network_static_ip') | title}} / {{$t('message.network_netmask') | title}}
          </span>
        </div>
        <div class="col-7">
          <vue-ip-input v-if="!staticIpReadOnly" class="ip-input" :ip="networkConfig.static_ip" :on-change="onStaticIpChange"/>
          <input v-else v-model="networkConfig.static_ip" disabled class="ip-input">
          /
          <input type="number" v-model="networkConfig.netmask" class="netmask-input"
            :disabled="staticIpReadOnly" min="0" max="32">
        </div>
      </b-form-row>

      <b-form-row>
        <div class="col-5">
          <span class="label label-default">{{$t('message.network_gateway') | title}}</span>
        </div>
        <div class="col-7">
          <vue-ip-input v-if="!gatewayReadOnly" :ip="networkConfig.gateway" :on-change="onGatewayChange"/>
          <input v-else v-model="networkConfig.gateway" disabled class="ip-input">
        </div>
      </b-form-row>

      <b-form-row v-for="(dnsItem, index) in networkConfig.dns">
        <div class="col-5">
          <span class="label label-default">{{$t('message.network_dns') | title}} {{ index + 1}}</span>
        </div>
        <div class="col-7">
          <vue-ip-input v-if='!dnsReadOnly' class="ip-input" :ip="dnsItem" :on-change="getDnsChangeHandler(index)"/>
          <input v-else v-model="dnsItem" disabled class="ip-input">
        </div>
        <button v-if="index == 0" :disabled="dnsReadOnly" @click="addDns" type="button" class="padmin-button add-button">
          {{ $t("message.button_new") | title }}
        </button>
        <button v-else :disabled="dnsReadOnly" @click="delDns(index)" type="button" class="padmin-button del-button">
          {{$t('message.button_delete') | title}}
        </button>
      </b-form-row>

      <b-form-row>
        <div class="col-5">
          <span class="label label-default">
            {{$t('message.network_search_domains') | title}}
          </span>
        </div>
        <div class="col-5">
          <tags-input element-id="seach-domains" v-model="networkConfig.search_domains"
            ref="search-domains-tags" :typeahead="false" :add-tags-on-comma="true" placeholder=""
            :add-tags-on-blur="true" :disabled="searchDomainsReadOnly" />
        </div>
        <div>
          {{$t('message.network_search_domains_info')}}
        </div>
      </b-form-row>

      <h4>
        {{$t("message.static_routes") | title}}
        <button :disabled="staticRoutesReadOnly" @click="addStaticRoute" type="button" class="padmin-button add-button">
          {{ $t("message.button_new") | title }}
        </button>
      </h4>

      <b-form-row v-if="networkConfig.static_routes.length">
        <div class="col-5">
          <span class="label label-default">{{$t('message.network_static_route_to') | title}}</span>
        </div>
        <div class="col-4">
          <span class="label label-default">{{$t('message.network_static_route_via') | title}}</span>
        </div>
        <div class="col-2">
          <span class="label label-default">{{$t('message.network_static_route_metric') | title}}</span>
        </div>
      </b-form-row>

      <b-form-row v-for="(routeItem, index) in networkConfig.static_routes">
        <div class="col-5">
          <vue-ip-input v-if="!staticRoutesReadOnly" class="ip-input" :ip="routeItem.to_ip" :on-change="getStaticRouteToChangeHandler(index)"/>
          <input v-else v-model="routeItem.to_ip" disabled class="ip-input" >
          /
          <input type="number" :disabled="staticRoutesReadOnly" v-model="networkConfig.static_routes[index].to_netmask" class="netmask-input">
        </div>
        <div class="col-4">
          <vue-ip-input v-if="!staticRoutesReadOnly" class="ip-input" :ip="routeItem.via" :on-change="getStaticRouteViaChangeHandler(index)"/>
          <input v-else v-model="routeItem.via" disabled class="ip-input">
        </div>
        <div class="col-2">
          <b-form-input type="number" :disabled="staticRoutesReadOnly" v-model="routeItem.metric" class="metric-input"/>
        </div>
        <div class="col-1">
          <button :disabled="staticRoutesReadOnly" @click="delStaticRoute(index)" type="button" class="padmin-button del-button">
            {{$t('message.button_delete') | title}}
          </button>
        </div>
      </b-form-row>

      <div v-if="formError" class="form-error">{{formError}}</div>

      <b-button variant="primary" type="button"
        class="padmin-button submit-button right" :disabled="commandInProgress" @click="save">
        <b-spinner v-if="commandInProgress" small type="grow"/>
        {{$t('message.button_save') | title}}
      </b-button>

      <b-button variant="secondary" type="button"
        class="padmin-button right" :disabled="commandInProgress" @click="verify">
        {{$t('message.button_verify') | title}}
      </b-button>
      <b-button v-if="initConfig.state == 'network set'" type="button"
        class="padmin-button next-button right" :disabled="commandInProgress" @click="next">
        {{$t('message.button_next') | title}}
      </b-button>
    </b-form>

    <ConsoleOutput v-if="commandInitiated" :result="result" :output="output"/>

    <b-modal ref="ip-change-redirect-modal" id="ip-change-redirect-modal-id"
      size="sm" ok-only :title="$t('message.ip_change_redirect_title')" @ok="ipChangeRedirect">
      {{$t('message.ip_change_redirect_desc')}}
    </b-modal>
  </div>
</template>

<script>
import {fromKeyValueToArray, arrayToKeyValue} from '../util'
import VueIpInput from "vue-ip-input"
import { get, post } from "../http"
import ConsoleOutput from "./ConsoleOutput"
import { EventBus } from "../event-bus.js"
import { validateIpAddress, validateIpAddressesOnSameSubnet, validateHostName } from "../util"

export default {
  name: "Network",

  components: {
    ConsoleOutput,
    "vue-ip-input": VueIpInput
  },

  data() {
    return {
      networkConfig: null,
      initConfig: null,
      formError: "",
      commandInProgress: false,
      commandInitiated: false,
      result: null,
      output: ""
    }
  },

  computed: {
    description() {
      let messageLookup = "message.network_description";
      let description = this.$t(messageLookup);
      if (description === messageLookup) {
        return ''
      }
      return description
    },
    staticIpReadOnly() {
      if (!this.initConfig || !this.networkConfig) return true
      if (this.initConfig['bios-mode'] && this.initConfig['bios-mode'] == 'bios') {
        return true
      }
      if (this.initConfig.mode == 'cluster') return true
      return false
    },
    gatewayReadOnly() {
      if (!this.initConfig || !this.networkConfig) return true
      if (this.initConfig['bios-mode'] && this.initConfig['bios-mode'] == 'bios') {
        return true
      }
      return false
    },

    staticRoutesReadOnly() {
      if (!this.initConfig || !this.networkConfig) return true
      if (this.initConfig['bios-mode'] && this.initConfig['bios-mode'] == 'bios') {
        return true
      }
      return false
    },

    searchDomainsReadOnly() {
      if (!this.initConfig || !this.networkConfig) return true
      if (this.initConfig['bios-mode'] && this.initConfig['bios-mode'] == 'bios') {
        if (this.initConfig.state == 'start') return false
        if (this.initConfig.state == 'initialized') return true
        if (this.networkConfig.search_domains && this.networkConfig.search_domains.length > 0) {
          return true
        }
      }
      return false
    },

    dnsReadOnly() {
      if (!this.initConfig || !this.networkConfig) return true
      if (this.initConfig['bios-mode'] && this.initConfig['bios-mode'] == 'bios') {
        if (this.initConfig.state == 'start') return false
        if (this.initConfig.state == 'initialized') return true
        if (this.networkConfig.dns && this.networkConfig.dns.length > 0) {
          return true
        }
      }
      return false
    }
  },

  mounted() {
    this.load();
  },

  methods: {
    load() {
      get("/network").then(
        resp => {
          this.networkConfig = resp.data
          this.networkConfig.search_domains = arrayToKeyValue(this.networkConfig.search_domains)
          if (this.networkConfig.dns.length == 0) {
            this.addDns()
          }
        },
        err => {
          console.log("err", err);
        }
      )
      this.loadInitConfig()
    },

    loadInitConfig() {
      get("/init").then(
        resp => {
          this.initConfig = resp.data

        },
        err => {
          console.log("err", err);
        }
      )
    },

    validate() {
      this.formError = "";

      if (!validateIpAddress(this.networkConfig.static_ip)) {
        this.formError = this.$t("message.error_invalid_ip") + ": " + this.networkConfig.static_ip
        return;
      }

      if (this.networkConfig.netmask < 0 || this.networkConfig.netmask > 32) {
        this.formError =
          this.$t("message.error_invalid_netmask") + ": " + this.networkConfig.netmask
        return
      }

      if (!validateIpAddress(this.networkConfig.gateway)) {
        this.formError = this.$t("message.error_invalid_ip") + ": " + this.networkConfig.gateway
        return
      }

      for (let iDns=0; iDns < this.networkConfig.dns.length; iDns ++) {
        let dnsAddress = this.networkConfig.dns[iDns]
        if (!validateIpAddress(dnsAddress)) {
          this.formError = this.$t("message.error_invalid_ip") + ": " + dnsAddress
          return
        }
      }

      for (let iSearchDomain=0; iSearchDomain < this.networkConfig.search_domains.length; iSearchDomain ++) {
        let searchDomain = this.networkConfig.search_domains[iSearchDomain]
        if (!validateHostName(searchDomain.value)) {
          this.formError = this.$t('message.error_invalid_domainname') + ': ' + searchDomain.value
          return
        }
      }

      for (let iStaticRoute=0; iStaticRoute < this.networkConfig.static_routes.length; iStaticRoute++) {
        let staticRoute = this.networkConfig.static_routes[iStaticRoute]

        if (!validateIpAddress(staticRoute.to_ip)) {
          this.formError = this.$t("message.error_invalid_ip") + ": " + staticRoute.to_ip
          return
        }

        if (staticRoute.to_netmask < 0 || staticRoute.to_netmask > 32) {
          this.formError = this.$t("message.error_invalid_netmask") + ": " + staticRoute.netmask
          return
        }

        if (!validateIpAddress(staticRoute.via)) {
          this.formError = this.$t("message.error_invalid_ip") + ": " + staticRoute.via
          return
        }

        if (!validateIpAddressesOnSameSubnet([this.networkConfig.static_ip, staticRoute.via], this.networkConfig.netmask)) {
          this.formError = this.$t("message.error_static_route_not_same_subnet") + ":" + staticRoute.via
          return
        }

        if (staticRoute.metric < 0 || staticRoute.metric > 100) {
          this.formError = this.$t("message.error_invalid_metric") + ": " + staticRoute.metric
          return
        }
      }
    },

    save() {
      this.result = null
      this.output = ''

      this.validate()
      if (this.formError) return
      const formData = new FormData()
      for (let field in this.networkConfig) {
        let fieldValue = this.networkConfig[field]
        if (field === 'static_routes') {
          formData.append('static_routes_json_str', JSON.stringify(this.networkConfig.static_routes))
        } else if (field === 'search_domains') {
          formData.append(field, fromKeyValueToArray(fieldValue))
        }
        else {
          formData.append(field, fieldValue)
        }
      }

      this.commandInProgress = true
      this.commandInitiated = true

      setTimeout(() => {
        if (this.commandInProgress) {
          this.$refs["ip-change-redirect-modal"].show()
        }
      }, 15000);

      post('/network', formData, {'Content-Type': 'multipart/form-data'}).then(
        resp => {
          this.commandInProgress = false
          this.result = resp.data.result
          this.output = resp.data.output
          if (this.resp.data.error) {
            this.formError = resp.data.error
          } else {
            this.loadInitConfig()
          }
        },
        err => {
          this.result = false
          this.commandInProgress = false
          console.log("err", err)
        }
      );
    },

    verify() {
      this.result = null
      this.output = ''
      this.commandInitiated = true
      this.commandInProgress = true
      get('/network/verify').then(
        resp => {
          this.commandInProgress = false
          this.result = resp.data.result
          this.output = resp.data.output
        },
        err => {
          this.commandInProgress = false
          this.result = false
        }
      )
    },

    onStaticIpChange(ip) {
      this.networkConfig.static_ip = ip.replace(/\s+/g, '')
    },

    onGatewayChange(ip) {
      this.networkConfig.gateway = ip.replace(/\s+/g, '')
    },

    getDnsChangeHandler(index) {
      let that = this
      return function (val) {
        that.$set(that.networkConfig.dns, index, val.replace(/\s+/g, ''))
        that.$set(that.networkConfig, 'dns', that.networkConfig.dns)
      }
    },

    addDns() {
      this.networkConfig.dns.push('')
    },

    delDns(index){
      this.networkConfig.dns.splice(index, 1)
    },

    addStaticRoute() {
      let newRoute = {'to': '', 'to_netmask': '24', 'via': '', 'metric': 100}
      if(!this.networkConfig.static_routes) {
        this.networkConfig.static_routes = []
      }
      this.networkConfig.static_routes.push(newRoute)
      //this.$set(this.snetworkConfig, 'static_routes', this.networkConfig.static_routes)
    },

    delStaticRoute(index){
      this.networkConfig.static_routes.splice(index, 1)
    },

    getStaticRouteToChangeHandler(index) {
      let that = this
      return function (val) {
        let staticRoute = that.networkConfig.static_routes[index]
        staticRoute.to_ip = val.replace(/\s+/g, '')
        that.$set(that.networkConfig.static_routes, index, staticRoute)
        that.$set(that.networkConfig, 'static_routes', that.networkConfig.static_routes)
      }
    },

    getStaticRouteViaChangeHandler(index) {
      let that = this
      return function (val) {
        let staticRoute = that.networkConfig.static_routes[index]
        staticRoute.via = val.replace(/\s+/g, '')
        that.$set(that.networkConfig.static_routes, index, staticRoute)
        that.$set(that.networkConfig, 'static_routes', that.networkConfig.static_routes)
      }
    },

    ipChangeRedirect() {
      window.location.replace('https://' + this.networkConfig.static_ip + '/padmin')
    },

    next() {
      EventBus.$emit('progress init')
    }
  }
}
</script>

<style>
@import "./../styles/networkComponent.css"
</style>
